import React, { useState, useEffect, useReducer } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  Panel,
  Row,
  Col,
  Uploader,
  Icon,
  Table,
  ButtonGroup,
  IconButton,
  Input,
  Message,
  Loader,
  Whisper,
  Popover,
} from 'rsuite'
import CustomIconMessageInfo from '../CustomIconMessageInfo'
import classNames from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import notification from '../../utils/notification'
import ConfirmWidget from '../confirm/ConfirmWidget'
import { doneLoading, startLoading } from '../../utils/loading'
import { cloneDeep, isEmpty } from 'lodash-es'
import {
  deleteEFormDocumentById,
  downloadFileByPath,
  getEFormDocumentPage,
  saveEFormDocument,
} from '../../redux/actions/eFormDocument'
import { DEFAULT_ERR_MSG, DELETE_SUCCESS_MSG, SAVE_SUCCESS_MSG } from '../../constants/msgText'
import {
  deleteClosureAttachmentNewById,
  getCloseOfProjectCoworkerAfterClosedForm,
  getClosureAttachmentNew,
  saveClosureAttachmentNew,
} from '../../redux/actions/closeOfProject'

// type "99" (เอกสารประกอบอื่นๆ) is the "other" category the user can add extra rows to
const OTHER_TYPE = '99'

// Normalize schema rows (schema.fileList.value) into working rows.
// - _rowKey: stable key because several rows come with id === null
// - isAddAnchor: the "other" template row that renders the "เพิ่ม" button
const mapRows = (values) =>
  (values || []).map((item) => {
    const isOther = String(item.type) === OTHER_TYPE
    return {
      ...item,
      _rowKey: item.id || 'row-' + uuidv4(),
      isEdit: false,
      isDeleteFile: false,
      isAdded: false,
      isAddAnchor: isOther && !item.id && !item.path && !item.name,
    }
  })

export default function ClosureAttachmentWidget({
  schema,
  requestId,
  header,
  isContract,
  disabledForm,
  isStaff,
  isUser,
  onValidateFile = () => {},
  onUploadError = () => {},
  onUploadSuccess = () => {},
  closeTypeSelected,
  isAfterClosed = false,
  ...props
}) {
  const { Column, HeaderCell, Cell } = Table
  const [formSchema, setFormSchema] = useState(schema)
  const [attachFiles, setAttachFiles] = useState([])
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [deleteObj, setDeleteObj] = useState()
  const { errors, control } = useForm()
  const projectId = sessionStorage.getItem('projectId')
  const [isLoading, setIsLoading] = useState(false)

  const [_, forceUpdate] = useReducer((x) => x + 1, 0)

  useEffect(() => {
    if (schema) {
      setFormSchema(schema)
    }
  }, [schema])

  useEffect(() => {
    if (schema?.fileList?.value?.length > 0) {
      setAttachFiles(mapRows(schema.fileList.value))
    }
  }, [schema?.fileList])

  useEffect(() => {
    setTimeout(() => {
      forceUpdate()
    }, 100)
  }, [schema])

  const onAddfileType = (anchorRow) => {
    const _attachFiles = cloneDeep(attachFiles)
    const newRow = {
      _rowKey: 'row-' + uuidv4(),
      id: 'add-' + uuidv4(),
      seqNo: 0,
      name: null,
      path: null,
      type: OTHER_TYPE,
      typeName: '',
      size: 0,
      selected: false,
      isRequired: false,
      isEdit: true,
      isAdded: true,
      isAddAnchor: false,
      enableEdit: true,
    }
    // insert just before the "other" template row so it stays last
    const anchorIndex = _attachFiles.findIndex((r) => r.isAddAnchor)
    _attachFiles.splice(anchorIndex >= 0 ? anchorIndex : _attachFiles.length, 0, newRow)
    setAttachFiles(_attachFiles)
  }

  const onDeleteAddFile = (rowData) => {
    const _attachFiles = cloneDeep(attachFiles)
    const index = _attachFiles.findIndex((r) => r._rowKey === rowData._rowKey)
    if (index >= 0) {
      _attachFiles.splice(index, 1)
    }
    setAttachFiles(_attachFiles)
  }

  const setRowData = (rowData, newData) => {
    setAttachFiles((state) =>
      state.map((item) => (item._rowKey === rowData._rowKey ? { ...item, ...newData } : item))
    )
  }

  const handleUpload = async (request) => {
    startLoading()
    setIsLoading(true)
    try {
      const response = await saveClosureAttachmentNew(projectId, request)
      if (response) {
        notification.open('success', {
          title: SAVE_SUCCESS_MSG,
        })
        const values = response?.model || []
        setAttachFiles(mapRows(values))
        onUploadSuccess(values)
      } else {
        notification.open('error', {
          title: response.model.errorMessage || DEFAULT_ERR_MSG,
        })
      }
    } catch (e) {
      console.log('e', e)
      notification.open('error', {
        title: e?.response?.data?.errorMessage || DEFAULT_ERR_MSG,
      })
    }
    setIsLoading(false)
    doneLoading()
  }

  const handleDelete = async (deleteObj) => {
    startLoading()
    setIsLoading(true)
    try {
      const response = await deleteClosureAttachmentNewById(projectId, deleteObj.id)
      if (response) {
        notification.open('success', {
          title: DELETE_SUCCESS_MSG,
        })

        if (isAfterClosed) {
          const res = await getCloseOfProjectCoworkerAfterClosedForm(projectId)
          const values = res?.forms?.requestNote?.closureAttachment?.fileList?.value || []
          setAttachFiles(mapRows(values))
          onUploadSuccess(values)
        } else {
          const res = await getClosureAttachmentNew(projectId)
          const values = res?.model || []
          setAttachFiles(mapRows(values))
          onUploadSuccess(values)
        }
      } else {
        notification.open('error', {
          title: response.model.errorMessage || 'ลบรายการไม่สำเร็จ',
        })
      }
    } catch (e) {
      console.log('e', e)
      notification.open('error', {
        title: e?.response?.data?.errorMessage || 'ลบรายการไม่สำเร็จ',
      })
    }
    setIsLoading(false)
    doneLoading()
  }

  const onDownload = async (fileObj) => {
    startLoading()
    try {
      const file = await downloadFileByPath(fileObj.path)
      const url = window.URL.createObjectURL(new Blob([file], { type: file.type }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileObj.name || fileObj.typeName)
      document.body.appendChild(link)
      link.click()
    } catch (e) {
      notification.open('error', {
        title: e?.response?.data?.errorMessage || 'ดาวน์โหลดล้มเหลว',
      })
    }
    doneLoading()
  }

  const onPreview = async (fileObj) => {
    startLoading()
    try {
      const file = await downloadFileByPath(fileObj.path)
      const url = window.URL.createObjectURL(new Blob([file], { type: file.type }))
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.setAttribute('value', fileObj.name || fileObj.typeName)
      document.body.appendChild(link)
      link.click()
    } catch (e) {
      notification.open('error', {
        title: e?.response?.data?.errorMessage || 'แสดงล้มเหลว',
      })
    }
    doneLoading()
  }

  const handleOnSave = (rowData) => {
    console.log('rowData', rowData)

    // return false
    const formData = new FormData()

    if (rowData.id == null && rowData.files == null && rowData.isDeleteFile == false) {
      return notification.open('error', {
        title: 'กรุณาแนบไฟล์',
        duration: 3000,
      })
    }

    rowData.files?.forEach((element) => {
      formData.append('Files', element.blobFile)
    })

    const fileId = rowData.id && rowData.id.indexOf('add-') === -1 ? rowData.id : ''
    formData.append('FileId', fileId)
    formData.append('MasterClosureAttachmentId', rowData.type || '')
    formData.append('DocumentName', rowData.typeName || rowData?.files[0]?.name || '')
    // formData.append('DocumentType', rowData.type || '')
    // formData.append('RequestId', requestId || '')
    formData.append('IsDeleteFile', rowData.isDeleteFile ? true : false)
    formData.append('isAfterClosed', isAfterClosed)

    handleUpload(formData)
  }

  return (
    <Panel bordered header={header || formSchema?.header} collapsible defaultExpanded>
      {!disabledForm && (
        <Message
          showIcon
          type="info"
          description={'ชนิดที่รองรับ : .jpg, .jpeg, .png, .pdf ขนาดไฟล์ไม่เกิน 50 MB'}
        />
      )}

      {formSchema?.description && (
        <Row style={{ marginTop: 10 }}>
          <CustomIconMessageInfo contents={formSchema.description} />
        </Row>
      )}

      <Row style={{ marginTop: 10 }}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Table
            autoHeight
            wordWrap
            bordered
            data={
              isAfterClosed && closeTypeSelected == 1
                ? attachFiles.filter((item) => item.type != '5')
                : closeTypeSelected == 1 || closeTypeSelected == undefined
                  ? attachFiles.filter((item) => item.type != '1')
                  : attachFiles
            }
            rowKey="_rowKey"
          >
            <Column width={70} align="center" fixed>
              <HeaderCell>ลำดับ</HeaderCell>
              <Cell>
                {(rowData, index) => {
                  if (!rowData.isAddAnchor) {
                    return index + 1
                  }
                }}
              </Cell>
            </Column>

            <Column minWidth={300} flexGrow={1}>
              <HeaderCell>เอกสารประกอบ</HeaderCell>
              <Cell>
                {(rowData) => {
                  if (rowData.isEdit && String(rowData.type) === OTHER_TYPE) {
                    return (
                      <Controller
                        name={`typeName_${rowData._rowKey}`}
                        control={control}
                        defaultValue={rowData.typeName}
                        rules={{ required: 'กรุณากรอก' }}
                        render={(props) => {
                          return (
                            <Input
                              className={classNames(
                                'rs-input',
                                errors[rowData._rowKey] && 'is-error'
                              )}
                              placeholder="ระบุชื่อเอกสาร"
                              value={props.value}
                              onChange={(e) => {
                                props.onChange(e)
                                setRowData(rowData, {
                                  typeName: e,
                                })
                              }}
                            />
                          )
                        }}
                      />
                    )
                  } else if (rowData.typeName) {
                    // if(rowData.type == '1' && closeTypeSelected == '1'){ //แจ้งขอเบิกเงิน = ไม่บังคับแนบ หลักฐานการโอนเงินคืน สสส.
                    //   return (
                    //     <span>
                    //       {rowData.typeName}
                    //     </span>
                    //   )
                    // }
                    return (
                      <span>
                        {rowData.isRequired && <span style={{ color: 'red' }}>*</span>}
                        {rowData.typeName}
                      </span>
                    )
                  } else {
                    return
                  }
                }}
              </Cell>
            </Column>

            <Column width={130} align="center">
              <HeaderCell>แนบเอกสาร</HeaderCell>
              <Cell>
                {(rowData) => {
                  if (rowData.path && !rowData.isDeleteFile) {
                    return (
                      <ButtonGroup>
                        <IconButton
                          appearance="ghost"
                          icon={<Icon icon="eye" className="bt-gost-icon-radius" />}
                          onClick={(e) => onPreview(rowData)}
                          disabled={isLoading}
                        />
                        <IconButton
                          appearance="ghost"
                          icon={<Icon icon="download" className="bt-gost-icon-none-radius" />}
                          onClick={(e) => onDownload(rowData)}
                          disabled={isLoading}
                        />

                        {!disabledForm && rowData.isEdit && (
                          <IconButton
                            appearance="ghost"
                            className="rs-btn-ghost-cancel"
                            icon={
                              <Icon icon="trash-o" className="bt-gost-icon-none-radius-cancel" />
                            }
                            onClick={(e) => {
                              setDeleteObj(rowData)
                              setShowConfirmDelete(true)
                              // setRowData(rowData, {
                              //   isDeleteFile: true,
                              // })
                            }}
                          />
                        )}
                      </ButtonGroup>
                    )
                  } else if (rowData.isEdit) {
                    return (
                      !disabledForm && (
                        <>
                          {rowData.isLoading && <Loader backdrop center />}
                          <Controller
                            name={`file_${rowData._rowKey}`}
                            control={control}
                            defaultValue={undefined}
                            render={(props) => {
                              if (props.value) {
                                return (
                                  <div
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <p>{props?.value[0]?.name || 'file name'}</p>

                                    <IconButton
                                      appearance="link"
                                      icon={<Icon icon="close" style={{ color: 'gray' }} />}
                                      onClick={(e) => {
                                        props.onChange()
                                        setRowData(rowData, {
                                          files: undefined,
                                        })
                                      }}
                                    />
                                  </div>
                                )
                              } else {
                                if (!rowData.enableEdit) {
                                  return
                                }
                                return (
                                  <Uploader
                                    accept=".jpg, .jpeg, .png, .pdf"
                                    draggable
                                    autoUpload={false}
                                    multiple={false}
                                    listType="picture"
                                    fileList={props.value || []}
                                    onChange={(fileList) => {
                                      if (isEmpty(fileList)) {
                                        props.onChange(undefined)
                                        setRowData(rowData, {
                                          files: undefined,
                                        })
                                      } else {
                                        const files = [fileList[fileList.length - 1]]
                                        props.onChange(files)
                                        setRowData(rowData, {
                                          files: files,
                                        })
                                      }
                                    }}
                                  >
                                    <button className="upload__button" style={{ marginTop: 0 }}>
                                      <Icon icon="upload" size="lg" />
                                    </button>
                                  </Uploader>
                                )
                              }
                            }}
                          />
                        </>
                      )
                    )
                  } else if (rowData.isAddAnchor && !disabledForm) {
                    return (
                      <IconButton
                        appearance="ghost"
                        icon={<Icon icon="plus" className="bt-gost-icon-none-radius" />}
                        onClick={(e) => onAddfileType(rowData)}
                        disabled={isLoading}
                      >
                        เพิ่ม
                      </IconButton>
                    )
                  }
                }}
              </Cell>
            </Column>

            {!disabledForm && (
              <Column width={150}>
                <HeaderCell>จัดการ</HeaderCell>
                <Cell style={{ marginRight: 20 }}>
                  {(rowData) => {
                    if (!rowData.enableEdit) {
                      return
                    }
                    if (rowData.isEdit) {
                      return (
                        <>
                          <Whisper
                            placement="top"
                            trigger="hover"
                            speaker={<Popover>บันทึก</Popover>}
                          >
                            <IconButton
                              appearance="ghost"
                              className="rs-btn-ghost"
                              style={{ marginBottom: 0 }}
                              icon={<Icon icon="save" className="bt-gost-icon-none-radius" />}
                              onClick={(e) => {
                                handleOnSave(rowData)
                              }}
                              disabled={isLoading}
                            />
                          </Whisper>
                          <Whisper
                            placement="top"
                            trigger="hover"
                            speaker={<Popover>ยกเลิก</Popover>}
                          >
                            <IconButton
                              appearance="ghost"
                              className="rs-btn-ghost-cancel"
                              style={{ marginBottom: 0 }}
                              icon={
                                <Icon icon="close" className="bt-gost-icon-none-radius-cancel" />
                              }
                              onClick={(e) => {
                                // a freshly added row that was never saved is dropped on cancel
                                if (rowData.isAdded) {
                                  onDeleteAddFile(rowData)
                                } else {
                                  setRowData(rowData, {
                                    isEdit: false,
                                    isDeleteFile: false,
                                  })
                                }
                              }}
                              disabled={isLoading}
                            />
                          </Whisper>
                        </>
                      )
                    } else {
                      return (
                        <>
                          {!rowData.isAddAnchor && (
                            <Whisper
                              placement="top"
                              trigger="hover"
                              speaker={<Popover>แก้ไข</Popover>}
                            >
                              <IconButton
                                appearance="ghost"
                                className="rs-btn-ghost-edit"
                                style={{ marginBottom: 0 }}
                                icon={
                                  <Icon icon="edit" className="bt-gost-icon-none-radius-edit" />
                                }
                                onClick={(e) => {
                                  setRowData(rowData, {
                                    isEdit: true,
                                  })
                                }}
                                disabled={isLoading}
                              />
                            </Whisper>
                          )}

                          {String(rowData.type) === OTHER_TYPE && !rowData.isAddAnchor && (
                            <Whisper
                              placement="top"
                              trigger="hover"
                              speaker={<Popover>ลบรายการ</Popover>}
                            >
                              <IconButton
                                appearance="ghost"
                                className="rs-btn-ghost-cancel"
                                style={{ marginBottom: 0 }}
                                icon={
                                  <Icon icon="trash" className="bt-gost-icon-none-radius-cancel" />
                                }
                                onClick={(e) => {
                                  if (rowData.id && rowData.id.indexOf('add-') === -1) {
                                    setDeleteObj(rowData)
                                    setShowConfirmDelete(true)
                                  } else {
                                    onDeleteAddFile(rowData)
                                  }
                                }}
                                disabled={isLoading}
                              />
                            </Whisper>
                          )}
                        </>
                      )
                    }
                  }}
                </Cell>
              </Column>
            )}
          </Table>
        </Col>
      </Row>

      <ConfirmWidget
        size={'sm'}
        title={`ยืนยันการลบ`}
        show={showConfirmDelete}
        detail={<span>ยืนยันการลบรายการ</span>}
        onCancle={() => {
          setShowConfirmDelete(false)
          setDeleteObj('')
        }}
        onConfirm={() => {
          setShowConfirmDelete(false)
          handleDelete(deleteObj)
        }}
      />
    </Panel>
  )
}
